import { orderLineModifiersTotal } from './orderLineTotals';
import { AppliedDiscount, IAppliedDiscount } from './AppliedDiscount';
import { IOrderModifierGroup, OrderModifierGroup } from './OrderModifierGroup';
import { ExternalOrderSource, KitchenSendPriority, KitchenStatus } from './types';
import {
    IWriteOffActors,
    IWriteOffHistoryEntry,
    readWriteOffActors,
    readWriteOffHistory,
} from './writeOffActors';

/**
 * Void information for items removed after kitchen commitment
 * Tracks why the item was voided, who authorized it, and food cost impact
 */
export interface IOrderItemVoidInfo extends IWriteOffActors {
    /** Reason for voiding (required for manager approval) */
    readonly reason: string;
    /** User ID who authorized the void (manager) — see `IWriteOffActors` for who DID it */
    readonly authorizedBy: string;
    /** When the void was performed */
    readonly voidedAt: Date;
    /** Whether food cost was incurred (true if item was past 'pending' in kitchen) */
    readonly foodCostIncurred: boolean;
}

/**
 * The least an object must have for {@link isVoidedOrderItem} to judge it.
 *
 * Structural on purpose: the same rule has to answer for an `OrderItem`
 * instance, a plain Firestore map read by a server service, and a
 * `Record<string, unknown>` on its way through `fromJSON`. A predicate that
 * only accepted the class would have been copied for the other two, which is
 * exactly how the repo ended up with four of them.
 */
export interface VoidableOrderItemFields {
    readonly kitchenStatus?: unknown;
    readonly voidInfo?: unknown;
}

/**
 * Is this line VOIDED — struck from the ticket, never sold?
 *
 * 🔴 ONE RULE, ONE WRITER. Before this, four different answers were in the
 * tree and they disagreed:
 *
 *   - `kitchenStatus === 'voided'`  — `checkSplitService.getActiveItems`, the
 *     POS's `Order.activeItems`
 *   - `voidInfo != null`            — `deliveryDispatchService` (the Dasher's
 *     bag contents) and `payments/orderDetail` (the L2/L3 itemization)
 *   - neither                       — `Order.subtotalBeforeDiscounts` and the
 *     warehouse sync, which sold voided food
 *
 * Both markers are real and neither implies the other:
 * `kitchenStateService.applyStatusUpdate` writes `voidInfo` only when the
 * void carried a REASON, so a reason-less void is `kitchenStatus: 'voided'`
 * with no `voidInfo`; and a document can carry `voidInfo` from a void that
 * wrote the marker before the status landed. Either one means voided, so the
 * rule is the union — and every caller gets the same, strictly-safer answer.
 */
export function isVoidedOrderItem(item: VoidableOrderItemFields | null | undefined): boolean {
    if (!item) return false;
    return item.kitchenStatus === 'voided' || item.voidInfo != null;
}

/** The lines that are still SOLD — everything {@link isVoidedOrderItem} rejects. */
export function activeOrderItems<T extends VoidableOrderItemFields>(items: readonly T[] | undefined): T[] {
    return (items ?? []).filter((item) => !isVoidedOrderItem(item));
}

export interface IOrderItem {
    readonly orderItemId: number;
    readonly orderItemGuid: string;
    /** Internal menu item ID. 0 indicates external/unmapped item (e.g., from Uber Eats) */
    readonly menuItemId: number;
    /** External item identifier for third-party platform orders */
    readonly externalItemId?: string;
    /** Platform cart line item ID (e.g., Uber Eats cart_item_id for resolve-fulfillment-issues) */
    readonly cartItemId?: string;
    /** Source platform for external items */
    readonly externalSource?: ExternalOrderSource;
    readonly name: string;
    readonly quantity: number;
    readonly price: number; // Unit price (base or selected size price)
    readonly modifierGroups: IOrderModifierGroup[];
    readonly discounts: IAppliedDiscount[];
    readonly isTaxable: boolean;
    readonly applicableTaxRateIds: number[];
    readonly notes?: string;
    readonly seatNumber?: number;
    readonly isPending: boolean;

    // Kitchen workflow fields
    readonly kitchenStatus: KitchenStatus;
    readonly sentToKitchenAt?: Date;
    readonly readyAt?: Date;
    readonly servedAt?: Date;
    readonly firedAt?: Date;
    readonly kitchenPriority: KitchenSendPriority;
    readonly kitchenTicketId?: string;
    readonly kitchenNotes?: string;
    readonly courseId?: number;

    /**
     * The prep stations this line actually routed to on its last send.
     *
     * 🔴 WRITTEN BY THE SERVER, DECLARED BY NOBODY. `stampTicketMetadata`
     * stamps this back onto every line after a fan-out and the POS reads it
     * (`OrderItem.resolvedPrepStationIds` in Dart), yet no TypeScript model
     * declared it — so every server reader reached it through an `any`, and a
     * typo would have compiled.
     *
     * It is normally a RECORD of what the menu decided, so the menu stays
     * authoritative and this is only consulted when the menu can route the line
     * NOWHERE. That case is an OPEN ITEM: it carries a synthetic negative
     * `menuItemId` matching no item and no group, so without this it fell into
     * the synthetic "General" bucket and printed at the host stand.
     */
    readonly resolvedPrepStationIds?: number[];

    /** The menu group this item was ordered from. Used to resolve
     *  group-level prep station overrides (MenuGroup.prepStationIds). */
    readonly menuGroupId?: number;

    // Void tracking (set when kitchenStatus → 'voided')
    readonly voidInfo?: IOrderItemVoidInfo;
    /** Voids and comps taken back on this line — see `IWriteOffHistoryEntry`. */
    readonly writeOffHistory?: readonly IWriteOffHistoryEntry[];

    // Check splitting & transfer fields
    readonly splitOriginGuid?: string;
    readonly transferredFromOrderGuid?: string;
    readonly transferredAt?: Date;
    readonly isShared?: boolean;
    readonly splitPartsCount?: number;
    /**
     * When the operator pressed HOLD on the till with this line unsent — "keep
     * it, don't send it yet". Written by the POS; the idle pin-out and the exit
     * prompt never cancel a held line. Absent on every line nobody held.
     */
    readonly heldAt?: Date;
    readonly isGroupPriced?: boolean;
    /**
     * The menu item was marked "Exclude from discounts" when this line was
     * rung. Written by the POS (and by online checkout from the repriced
     * cart); discounts and promo codes skip the line, comps still reach it.
     * Absent on every line whose item was discountable.
     */
    readonly excludeFromDiscounts?: boolean;

    /**
     * COMPOSITE ITEMS. `isComposite` marks the PARENT line — the priced
     * container of a "choose N" composite (a Lunch Special). It carries the
     * whole price and must NEVER reach a prep station or a kitchen ticket.
     * `bundleParentGuid` is set on every CHILD and holds the parent's
     * `orderItemGuid`.
     *
     * Children are ordinary order lines on purpose: `Order.courseStates` is
     * keyed by `courseId` and `kitchenTicketEngine` groups printed sections by
     * it, so only a real line can be coursed, fired and routed on its own.
     *
     * Both absent = an ordinary line, unchanged in every path.
     * See packages/models/src/menus/compositeItemResolver.ts.
     */
    readonly isComposite?: boolean;
    readonly bundleParentGuid?: string;

    // Tip attribution (segment-aware / %-of-sales pooling)
    /** Sales category snapshot, stamped at ring-up. Falls back to
     *  `menuItemId → Item.salesCategoryId` resolution at aggregation time. */
    readonly salesCategoryId?: number;
    /** Employee credited with this line for %-of-sales tip-out. Defaults to the
     *  check owner when unset (the ringer is NOT assumed to be the server).
     *  Distinct from `CheckItem.assignedByEmployeeId`, which is the actor who
     *  assigned/transferred an item during check-splitting (audit), not the
     *  server credited for sales attribution. */
    readonly attributedEmployeeId?: number;
    /**
     * Uber Eats per-item price type from `payment.payment_detail.item_charges
     * .price_breakdown[].price_type` (e.g. `'BASE_PRICE'`, `'CUSTOMIZATION'`).
     * Absent for non-Uber sources.
     */
    readonly priceType?: string;
}

export interface IOrderItemData {
    orderItemId: number;
    orderItemGuid: string;
    /** Internal menu item ID. 0 indicates external/unmapped item (e.g., from Uber Eats) */
    menuItemId: number;
    /** External item identifier for third-party platform orders */
    externalItemId?: string;
    /** Platform cart line item ID (e.g., Uber Eats cart_item_id) */
    cartItemId?: string;
    /** Source platform for external items */
    externalSource?: ExternalOrderSource;
    name: string;
    quantity?: number;
    price?: number; // Unit price (base or selected size price)
    modifierGroups?: OrderModifierGroup[];
    discounts?: AppliedDiscount[];
    isTaxable?: boolean;
    applicableTaxRateIds?: number[];
    notes?: string;
    seatNumber?: number;
    isPending?: boolean;
    kitchenStatus?: KitchenStatus;
    sentToKitchenAt?: Date;
    readyAt?: Date;
    servedAt?: Date;
    firedAt?: Date;
    kitchenPriority?: KitchenSendPriority;
    kitchenTicketId?: string;
    kitchenNotes?: string;
    courseId?: number;
    /** See {@link IOrderItem.resolvedPrepStationIds}. */
    resolvedPrepStationIds?: number[];
    menuGroupId?: number;
    voidInfo?: IOrderItemVoidInfo;
    writeOffHistory?: readonly IWriteOffHistoryEntry[];
    // Check splitting & transfer fields
    splitOriginGuid?: string;
    transferredFromOrderGuid?: string;
    transferredAt?: Date;
    isShared?: boolean;
    splitPartsCount?: number;
    heldAt?: Date;
    isGroupPriced?: boolean;
    excludeFromDiscounts?: boolean;
    isComposite?: boolean;
    bundleParentGuid?: string;
    // Tip attribution
    salesCategoryId?: number;
    attributedEmployeeId?: number;
    /** Uber Eats per-item price type — see IOrderItem.priceType. */
    priceType?: string;
}

export class OrderItem implements IOrderItem {
    readonly orderItemId: number;
    readonly orderItemGuid: string;
    readonly menuItemId: number;
    readonly externalItemId?: string;
    readonly cartItemId?: string;
    readonly externalSource?: ExternalOrderSource;
    readonly name: string;
    readonly quantity: number;
    readonly price: number; // Unit price (base or selected size price)
    readonly modifierGroups: OrderModifierGroup[];
    readonly discounts: AppliedDiscount[];
    readonly isTaxable: boolean;
    readonly applicableTaxRateIds: number[];
    readonly notes?: string;
    readonly seatNumber?: number;
    readonly isPending: boolean;

    // Kitchen workflow fields
    readonly kitchenStatus: KitchenStatus;
    readonly sentToKitchenAt?: Date;
    readonly readyAt?: Date;
    readonly servedAt?: Date;
    readonly firedAt?: Date;
    readonly kitchenPriority: KitchenSendPriority;
    readonly kitchenTicketId?: string;
    readonly kitchenNotes?: string;
    /** See {@link IOrderItem.resolvedPrepStationIds}. */
    readonly resolvedPrepStationIds?: number[];
    readonly courseId?: number;
    readonly menuGroupId?: number;

    // Void tracking
    readonly voidInfo?: IOrderItemVoidInfo;
    readonly writeOffHistory?: readonly IWriteOffHistoryEntry[];

    // Check splitting & transfer fields
    readonly splitOriginGuid?: string;
    readonly transferredFromOrderGuid?: string;
    readonly transferredAt?: Date;
    readonly isShared?: boolean;
    readonly splitPartsCount?: number;
    /**
     * When the operator pressed HOLD on the till with this line unsent — "keep
     * it, don't send it yet". Written by the POS; the idle pin-out and the exit
     * prompt never cancel a held line. Absent on every line nobody held.
     */
    readonly heldAt?: Date;
    readonly isGroupPriced?: boolean;
    readonly excludeFromDiscounts?: boolean;
    readonly isComposite?: boolean;
    readonly bundleParentGuid?: string;

    // Tip attribution
    readonly salesCategoryId?: number;
    readonly attributedEmployeeId?: number;
    /** Uber Eats per-item price type — see IOrderItem.priceType. */
    readonly priceType?: string;

    /**
     * Coerce `menuItemId` to the number the type already claims it is.
     *
     * 🔴 THE ASYMMETRY THIS CLOSES. The WRITE path took `data.menuItemId`
     * verbatim while the READ path (`fromJSON`) THROWS on anything non-numeric.
     * So a null could be persisted but never read back — and because the throw
     * happens inside `snapshot.docs.map()`, ONE such row made the entire orders
     * collection unreadable. Eleven items across nine live orders did exactly
     * that (Manje Lakay, 2026-08-04 → 08-06); the cart→order path passed
     * `cartItem.menuItemId` straight through and `CartItem.fromJSON` narrowed
     * every sibling field EXCEPT this one.
     *
     * ⚠️ IT COERCES, IT DOES NOT THROW. This runs while a guest is checking
     * out. Throwing would turn a mislinked line into a failed order — trading a
     * display defect for a lost sale. `0` is the sentinel `OrderItemSchema`
     * already documents for an unmapped line, so the row stays readable and
     * honest about what is not known.
     *
     * ⚠️ IT IS LOUD. A first-party line reaching here is a REAL defect upstream
     * (the cart was built without an item id), and a silent `?? 0` is what let
     * this run unnoticed for days. `console` is deliberate: `models` is the
     * lowest package and has no logger — matching `Order.ts` and `AppliedTax.ts`.
     */
    private static coerceMenuItemId(value: unknown, name: unknown): number {
        if (typeof value === 'number' && !Number.isNaN(value)) return value;
        if (typeof value === 'string') {
            const parsed = Number(value);
            if (!Number.isNaN(parsed)) return parsed;
        }
        console.error(
            'OrderItem: menuItemId is not a number — storing 0 (unmapped). ' +
                'A first-party line reaching this point means the cart was built without ' +
                'an item id.',
            { received: value, type: typeof value, itemName: name },
        );
        return 0;
    }

    constructor(data: IOrderItemData) {
        this.orderItemId = data.orderItemId;
        this.orderItemGuid = data.orderItemGuid;
        this.menuItemId = OrderItem.coerceMenuItemId(data.menuItemId, data.name);
        this.externalItemId = data.externalItemId;
        this.cartItemId = data.cartItemId;
        this.externalSource = data.externalSource;
        this.name = data.name;
        this.quantity = data.quantity ?? 1;
        this.price = data.price ?? 0;
        this.modifierGroups = data.modifierGroups ?? [];
        this.discounts = data.discounts ?? [];
        this.isTaxable = data.isTaxable ?? true;
        this.applicableTaxRateIds = data.applicableTaxRateIds ?? [];
        this.notes = data.notes;
        this.seatNumber = data.seatNumber;
        this.isPending = data.isPending ?? true;
        this.kitchenStatus = data.kitchenStatus ?? 'pending';
        this.sentToKitchenAt = data.sentToKitchenAt;
        this.readyAt = data.readyAt;
        this.servedAt = data.servedAt;
        this.firedAt = data.firedAt;
        this.kitchenPriority = data.kitchenPriority ?? 'normal';
        this.kitchenTicketId = data.kitchenTicketId;
        this.resolvedPrepStationIds = data.resolvedPrepStationIds;
        this.kitchenNotes = data.kitchenNotes;
        this.courseId = data.courseId;
        this.menuGroupId = data.menuGroupId;
        this.voidInfo = data.voidInfo;
        this.writeOffHistory = data.writeOffHistory;
        // Check splitting & transfer fields
        this.splitOriginGuid = data.splitOriginGuid;
        this.transferredFromOrderGuid = data.transferredFromOrderGuid;
        this.transferredAt = data.transferredAt;
        this.isShared = data.isShared;
        this.splitPartsCount = data.splitPartsCount;
        this.heldAt = data.heldAt;
        this.isGroupPriced = data.isGroupPriced;
        this.excludeFromDiscounts = data.excludeFromDiscounts;
        this.isComposite = data.isComposite;
        this.bundleParentGuid = data.bundleParentGuid;
        this.salesCategoryId = data.salesCategoryId;
        this.attributedEmployeeId = data.attributedEmployeeId;
        this.priceType = data.priceType;
    }

    /** Check if this item is from an external platform (not internal menu) */
    get isExternalItem(): boolean {
        return this.menuItemId === 0 || !!this.externalItemId;
    }

    // Calculated getters
    /** Unit price with options — `orderLineModifiersTotal`, the one rule. */
    get priceWithModifiers(): number {
        return this.price + orderLineModifiersTotal(this);
    }

    get totalOriginalPrice(): number {
        return this.priceWithModifiers * this.quantity;
    }

    get totalDiscountAmount(): number {
        return this.discounts.reduce((total, discount) => total + discount.amountApplied, 0);
    }

    get priceAfterItemDiscounts(): number {
        return this.totalOriginalPrice - this.totalDiscountAmount;
    }

    // Kitchen workflow computed properties
    get canSendToKitchen(): boolean {
        return this.kitchenStatus === 'pending';
    }

    get isSentToKitchen(): boolean {
        return ['sentToKitchen', 'inPreparation', 'ready', 'served'].includes(this.kitchenStatus);
    }

    get isReadyToServe(): boolean {
        return this.kitchenStatus === 'ready';
    }

    get isServed(): boolean {
        return this.kitchenStatus === 'served';
    }

    get canBeCancelled(): boolean {
        return this.kitchenStatus === 'pending';
    }

    /** See {@link isVoidedOrderItem} — the one rule, shared with every server path. */
    get isVoided(): boolean {
        return isVoidedOrderItem(this);
    }

    get requiresVoid(): boolean {
        return this.isSentToKitchen && this.kitchenStatus !== 'served';
    }

    get kitchenStatusDisplay(): string {
        switch (this.kitchenStatus) {
            case 'pending':
                return 'Pending Kitchen';
            case 'sentToKitchen':
                return 'Sent to Kitchen';
            case 'inPreparation':
                return 'Being Prepared';
            case 'ready':
                return 'Ready';
            case 'served':
                return 'Served';
            case 'fired':
                return 'Fired';
            case 'cancelled':
                return 'Cancelled';
            case 'voided':
                return 'Voided';
            default:
                return 'Unknown';
        }
    }

    toJSON(): IOrderItem {
        return {
            orderItemId: this.orderItemId,
            orderItemGuid: this.orderItemGuid,
            menuItemId: this.menuItemId,
            externalItemId: this.externalItemId,
            cartItemId: this.cartItemId,
            externalSource: this.externalSource,
            name: this.name,
            quantity: this.quantity,
            price: this.price,
            modifierGroups: this.modifierGroups.map(g => g.toJSON()),
            discounts: this.discounts.map(d => d.toJSON()),
            isTaxable: this.isTaxable,
            applicableTaxRateIds: this.applicableTaxRateIds,
            notes: this.notes,
            seatNumber: this.seatNumber,
            isPending: this.isPending,
            kitchenStatus: this.kitchenStatus,
            sentToKitchenAt: this.sentToKitchenAt,
            readyAt: this.readyAt,
            servedAt: this.servedAt,
            firedAt: this.firedAt,
            kitchenPriority: this.kitchenPriority,
            kitchenTicketId: this.kitchenTicketId,
            resolvedPrepStationIds: this.resolvedPrepStationIds,
            kitchenNotes: this.kitchenNotes,
            courseId: this.courseId,
            menuGroupId: this.menuGroupId,
            voidInfo: this.voidInfo,
            writeOffHistory: this.writeOffHistory,
            // Check splitting & transfer fields
            splitOriginGuid: this.splitOriginGuid,
            transferredFromOrderGuid: this.transferredFromOrderGuid,
            transferredAt: this.transferredAt,
            isShared: this.isShared,
            splitPartsCount: this.splitPartsCount,
            heldAt: this.heldAt,
            isGroupPriced: this.isGroupPriced,
            excludeFromDiscounts: this.excludeFromDiscounts,
            isComposite: this.isComposite,
            bundleParentGuid: this.bundleParentGuid,
            salesCategoryId: this.salesCategoryId,
            attributedEmployeeId: this.attributedEmployeeId,
            priceType: this.priceType,
        };
    }

    static fromJSON(data: Record<string, unknown>): OrderItem {
        // Helper to coerce to number (handles both numbers and numeric strings)
        const toNumber = (value: unknown, fieldName: string): number => {
            if (typeof value === 'number') return value;
            if (typeof value === 'string') {
                const num = Number(value);
                if (!isNaN(num)) return num;
            }
            throw new Error(`OrderItem.fromJSON: ${fieldName} must be a number (got ${typeof value}: ${value})`);
        };

        const orderItemId = toNumber(data.orderItemId, 'orderItemId');
        const menuItemId = toNumber(data.menuItemId, 'menuItemId');

        if (typeof data.orderItemGuid !== 'string') {
            throw new Error("OrderItem.fromJSON: orderItemGuid must be a string");
        }
        if (typeof data.name !== 'string') {
            throw new Error("OrderItem.fromJSON: name must be a string");
        }

        const modifierGroups = Array.isArray(data.modifierGroups)
            ? data.modifierGroups.map((g: any) => OrderModifierGroup.fromJSON(g))
            : [];

        const discounts = Array.isArray(data.discounts)
            ? data.discounts.map((d: any) => AppliedDiscount.fromJSON(d))
            : [];

        const parseDate = (value: unknown): Date | undefined => {
            if (value instanceof Date) return value;
            if (typeof value === 'string') return new Date(value);
            // Handle Firestore Timestamp objects. Without this branch a raw
            // Timestamp (the common shape, since Order.fromJSON passes item maps
            // straight through) returned undefined — nulling sentToKitchenAt /
            // readyAt / servedAt / firedAt / transferredAt and, worse, making
            // voidInfo.voidedAt fall back to a FABRICATED `new Date()`. Mirrors
            // the identical helper in Order/Check/TransferRecord/Cart.
            if (
                value &&
                typeof value === 'object' &&
                'toDate' in value &&
                typeof (value as any).toDate === 'function'
            ) {
                return (value as any).toDate();
            }
            return undefined;
        };

        // Helper to coerce to number with fallback
        const toNumberOrDefault = (value: unknown, defaultValue: number): number => {
            if (typeof value === 'number') return value;
            if (typeof value === 'string') {
                const num = Number(value);
                if (!isNaN(num)) return num;
            }
            return defaultValue;
        };

        return new OrderItem({
            orderItemId: orderItemId,
            orderItemGuid: data.orderItemGuid,
            menuItemId: menuItemId,
            externalItemId: data.externalItemId as string | undefined,
            cartItemId: data.cartItemId as string | undefined,
            externalSource: data.externalSource as ExternalOrderSource | undefined,
            name: data.name,
            quantity: toNumberOrDefault(data.quantity, 1),
            price: toNumberOrDefault(data.price, 0),
            modifierGroups,
            discounts,
            isTaxable: typeof data.isTaxable === 'boolean' ? data.isTaxable : true,
            applicableTaxRateIds: Array.isArray(data.applicableTaxRateIds) ? data.applicableTaxRateIds : [],
            notes: data.notes as string | undefined,
            seatNumber: data.seatNumber as number | undefined,
            isPending: typeof data.isPending === 'boolean' ? data.isPending : true,
            kitchenStatus: (data.kitchenStatus as KitchenStatus) ?? 'pending',
            sentToKitchenAt: parseDate(data.sentToKitchenAt),
            readyAt: parseDate(data.readyAt),
            servedAt: parseDate(data.servedAt),
            firedAt: parseDate(data.firedAt),
            kitchenPriority: (data.kitchenPriority as KitchenSendPriority) ?? 'normal',
            kitchenTicketId: data.kitchenTicketId as string | undefined,
            kitchenNotes: data.kitchenNotes as string | undefined,
            courseId: data.courseId as number | undefined,
            menuGroupId: data.menuGroupId as number | undefined,
            voidInfo: data.voidInfo ? {
                reason: (data.voidInfo as any).reason as string,
                authorizedBy: (data.voidInfo as any).authorizedBy as string,
                voidedAt: parseDate((data.voidInfo as any).voidedAt) ?? new Date(),
                foodCostIncurred: (data.voidInfo as any).foodCostIncurred as boolean ?? false,
                // WHO voided it and whether a PIN approved it — dropped here
                // until 2026-09-22, so no reader could show them.
                ...readWriteOffActors(data.voidInfo),
            } : undefined,
            writeOffHistory: data.writeOffHistory === undefined
                ? undefined
                : readWriteOffHistory(data.writeOffHistory),
            // Check splitting & transfer fields
            splitOriginGuid: data.splitOriginGuid as string | undefined,
            transferredFromOrderGuid: data.transferredFromOrderGuid as string | undefined,
            transferredAt: parseDate(data.transferredAt),
            isShared: data.isShared as boolean | undefined,
            splitPartsCount: data.splitPartsCount as number | undefined,
            heldAt: parseDate(data.heldAt),
            isGroupPriced: data.isGroupPriced as boolean | undefined,
            excludeFromDiscounts: data.excludeFromDiscounts as boolean | undefined,
            isComposite: data.isComposite as boolean | undefined,
            bundleParentGuid: data.bundleParentGuid as string | undefined,
            salesCategoryId: data.salesCategoryId as number | undefined,
            attributedEmployeeId: data.attributedEmployeeId as number | undefined,
            priceType: data.priceType as string | undefined,
        });
    }

    toString(): string {
        return `OrderItem(id: ${this.orderItemId}, name: "${this.name}", qty: ${this.quantity}, price: $${this.priceWithModifiers.toFixed(2)})`;
    }
}
